import React, { useEffect, useState } from "react";
import { Badge, Button, Modal } from "antd";

const storageKey = (userUid: string) =>
  `wzee:document-intake-intro:v1:${userUid}`;

export function DocumentIntakeNewBadge({
  compact = false,
}: {
  compact?: boolean;
}) {
  // Give the release badge a fixed lifetime rather than leaving it new forever.
  return Date.now() < Date.UTC(2026, 10, 12) ? (
    <Badge
      count="NEW"
      color="#2563eb"
      style={{
        boxShadow: "none",
        fontSize: compact ? 9 : 10,
        fontWeight: 700,
        letterSpacing: "0.5px",
        height: compact ? 16 : 18,
        lineHeight: compact ? "16px" : "18px",
        minWidth: 0,
        padding: "0 6px",
        marginInlineStart: compact ? 0 : 8,
        verticalAlign: "middle",
      }}
    />
  ) : null;
}

export default function DocumentIntakeIntro({ userUid }: { userUid?: string }) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    setOpen(false);
    if (!userUid) return;
    try {
      setOpen(localStorage.getItem(storageKey(userUid)) !== "seen");
    } catch {
      // The guide remains available when browser storage is unavailable.
    }
  }, [userUid]);
  const dismiss = () => {
    if (userUid) {
      try {
        localStorage.setItem(storageKey(userUid), "seen");
      } catch {
        /* Storage may be blocked. */
      }
    }
    setOpen(false);
  };
  return (
    <>
      <Button type="link" onClick={() => setOpen(true)}>
        How document intake works
      </Button>
      <Modal
        open={open}
        title="Turn documents into reviewed estate information"
        onCancel={dismiss}
        width={560}
        footer={[
          <Button key="start" type="primary" onClick={dismiss}>
            Got it
          </Button>,
        ]}
      >
        <p>
          Use certificates and statements to prepare accurate estate records for
          probate forms.
        </p>
        <ol className="sd-intro-steps">
          <li>
            <strong>Upload, then extract</strong>
            <p>
              Upload your documents, then choose Extract information when you
              are ready. Extraction shares the document with our AI providers;
              uploading alone does not.
            </p>
          </li>
          <li>
            <strong>Compare before saving</strong>
            <p>
              Check the original document and proposed values. Choose an
              existing person or financial record, or create a new one. Keep
              saved values when the extracted information should not replace
              them.
            </p>
          </li>
          <li>
            <strong>Confirm or save for later</strong>
            <p>
              An executor or administrator confirms reviewed values into estate
              records used by probate forms. Save for later keeps information
              unverified without changing estate records.
            </p>
          </li>
        </ol>
        <p>You can reopen this guide using How document intake works.</p>
      </Modal>
    </>
  );
}
